import React from 'react';
import moment from 'moment';
import { Table, TableBody, TableCell, TableRow, Paper, TextField, Button, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EstimatedDateSummaryTableProps } from '../../helper/EstimatedShippingDate.types';
import Helper from '../../../../utils/helper';
import { ESDSUM } from '../../estimatedShippingDate.classes';

export const EstimatedDateSummaryTable: React.FC<EstimatedDateSummaryTableProps> = ({
    date,
    backlog,
    estimatedShippedDay,
    estimatedShippedDayFromBackoffice,
    onUpdateSummaryInput,
    onSaveBacklog,
    onSaveEstimations,
    isEstimationsSaveDisabled,
    isLoading,
}) => {
    const { t } = useTranslation();

    const isBacklogSaveDisabled = estimatedShippedDayFromBackoffice === '0' || estimatedShippedDayFromBackoffice === 0;

    const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const sanitizedValue = event.target.value.replace(/[^0-9]/g, '');
        const sanitizedNumber = sanitizedValue ? Number(sanitizedValue) : 0;
        const isAllZeros = /^0+$/.test(sanitizedValue);

        if (sanitizedNumber > 9 || isAllZeros) return;

        onUpdateSummaryInput(field, sanitizedValue);
    };

    return (
        <Paper className={ESDSUM.paper} elevation={4} sx={{ padding: '16px', borderRadius: '8px' }}>
            <Table className={ESDSUM.table} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                <TableBody>
                    <TableRow
                        className={ESDSUM.summaryRow}
                        sx={{
                            '& .MuiTableCell-root': {
                                border: 0,
                            },
                        }}
                    >
                        <TableCell
                            className={ESDSUM.cellTodayLabel}
                            sx={{
                                fontWeight: 'bold',
                                textAlign: 'center',
                                backgroundColor: '#f5f5f5',
                                borderRight: '1px solid #e0e0e0 !important',
                            }}
                        >
                            {moment(date).locale('tr').format('DD.MM.YYYY')}
                        </TableCell>
                        <TableCell className={ESDSUM.cellBacklogHeader} sx={{ fontWeight: 'bold', textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                            {t('cargoManagement.estimatedShippingDate.backlog')}
                        </TableCell>
                        <TableCell className={ESDSUM.cellBacklogValue} sx={{ textAlign: 'center' }}>{Helper.formatNumberWithDots(backlog)}</TableCell>
                        <TableCell className={ESDSUM.cellShippedFromBoHeader} sx={{ fontWeight: 'bold', textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                            {t('cargoManagement.estimatedShippingDate.estimatedShippedDayFromBackoffice')}
                        </TableCell>
                        <TableCell>
                            <TextField
                                className={ESDSUM.inputTextfieldShippedFromBo}
                                value={estimatedShippedDayFromBackoffice}
                                onChange={handleInputChange('estimatedShippedDayFromBackoffice')}
                                size="small"
                                error={
                                    estimatedShippedDayFromBackoffice === 0 || estimatedShippedDayFromBackoffice === '0'
                                }
                                slotProps={{
                                    htmlInput: {
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*',
                                        max: 9,
                                    },
                                }}
                                sx={{ width: '100%', '& input': { textAlign: 'center' } }}
                            />
                        </TableCell>
                        <TableCell className={ESDSUM.cellEstimatedShippedHeader} sx={{ fontWeight: 'bold', textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                            {t('cargoManagement.estimatedShippingDate.estimatedShippedDay')}
                        </TableCell>
                        <TableCell className={ESDSUM.cellEstimatedShippedValue} sx={{ textAlign: 'center' }}>{estimatedShippedDay || '-'}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <Box className={ESDSUM.actionsRow} sx={{ display: 'flex', flexDirection: 'row' }}>
                <Button
                    className={ESDSUM.inputButtonSaveEstimations}
                    variant="contained"
                    size="large"
                    disabled={isEstimationsSaveDisabled || isLoading}
                    sx={{
                        fontWeight: 'bold',
                        marginTop: '24px',
                        display: 'block',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        padding: '12px 32px !important',
                        width: 'fit-content',
                        color: '#fff',
                        textTransform: 'none',
                        fontSize: '14px',
                        minWidth: '200px',
                        borderRadius: '8px',
                        backgroundColor: isEstimationsSaveDisabled ? '#cccccc' : '#1976d2',
                        '&:hover': {
                            backgroundColor: isEstimationsSaveDisabled ? '#cccccc' : '#1565c0',
                        },
                    }}
                    onClick={onSaveEstimations}
                >
                    {t('cargoManagement.estimatedShippingDate.saveEstimations')}
                </Button>
                <Button
                    className={ESDSUM.inputButtonSaveBacklog}
                    variant="contained"
                    size="large"
                    disabled={isBacklogSaveDisabled || isLoading}
                    sx={{
                        fontWeight: 'bold',
                        marginTop: '24px',
                        display: 'block',
                        marginRight: 'auto',
                        padding: '12px 32px !important',
                        width: 'fit-content',
                        color: '#fff',
                        textTransform: 'none',
                        fontSize: '14px',
                        minWidth: '200px',
                        borderRadius: '8px',
                        marginLeft: '16px',
                        backgroundColor: isBacklogSaveDisabled ? '#cccccc' : '#1976d2',
                        '&:hover': {
                            backgroundColor: isBacklogSaveDisabled ? '#cccccc' : '#1565c0',
                        },
                    }}
                    onClick={onSaveBacklog}
                >
                    {t('cargoManagement.estimatedShippingDate.saveBacklog')}
                </Button>
            </Box>
        </Paper>
    );
};
